<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useConfirm } from 'primevue/useconfirm';
import type { MenuItem } from 'primevue/menuitem';
import { FilterMatchMode } from '@primevue/core/api';
import Badge from 'primevue/badge';
import Button from 'primevue/button';
import Column from 'primevue/column';
import ConfirmDialog from 'primevue/confirmdialog';
import DataTable, { DataTableRowClickEvent } from 'primevue/datatable';
import InputText from 'primevue/inputtext';
import Skeleton from 'primevue/skeleton';
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';
import BriefcaseIcon from '@primeicons/vue/briefcase';
import FileIcon from '@primeicons/vue/file';
import GlobeIcon from '@primeicons/vue/globe';
import MinusIcon from '@primeicons/vue/minus';
import PlusIcon from '@primeicons/vue/plus';
import SitemapIcon from '@primeicons/vue/sitemap';
import {
  EventBusEvents,
  useCrm,
  useEndpoint,
  useEventBus,
  useOpportunities,
  useThemeOverrides,
  type CrmDetailTabName,
} from '@apparatix/composables';
import type {
  AccountToCopyTo,
  ApiResponseBase,
  CopyToAdvertiserResponse,
  DocumentAuditTrailsResponse,
  DocumentDetails,
  DocumentDetailsData,
  DocumentHistory,
  DocumentVersion,
} from '@apparatix/types/Documents';
import Modal from '@apparatix/Components/Modal.vue';
import FormSkeleton from '@apparatix/Components/FormSkeleton.vue';
import OfferSubmissionSection from '@apparatix/Components/OfferSubmission/OfferSubmissionSection.vue';
import { Section, Typography } from '@apparatix/library/core';
import { button as greenButton } from '@apparatix/theme/Green';
import { useAccountStore } from '@apparatix/stores/useAccountStore';
import { useDocumentStore } from '@apparatix/stores/useDocumentStore';
import { CrmDrawerDataReadyEvent, DetailTabName, FlyoutNavigationContext } from './types';
import DetailDrawerHeader from './DetailDrawerHeader.vue';
import ConfigGate from '@apparatix/library/ConfigGate.vue';
import AssociationListContainer from './AssociationListContainer.vue';
import AssociationListItem from './AssociationListItem.vue';
import DocumentDealsDocsPanel from './DocumentDealsDocsPanel.vue';
import { SharedStyles } from './SharedStyles';

type Props = {
  id: number,
  activeTab?: DetailTabName,
}

const props = defineProps<Props>();

const { datatable } = useThemeOverrides();

const copyAdvertiserVisible = ref(false);
const activeTab = ref<DetailTabName>(props.activeTab || 'overview');

const store = useDocumentStore();
const accountStore = useAccountStore();

const { handleDetailTabUpdate } = useCrm();
const { emitEvent } = useEventBus();
const confirm = useConfirm();
const route = useRoute();
const router = useRouter();

const documentDetails = ref<DocumentDetails>();
const documentHistory = ref<DocumentHistory[]>([]);
const documentVersions = ref<DocumentVersion[]>([]);

const mobileMenuItems = computed<MenuItem[]>(() => {
  if (!documentDetails.value?.type) return [];

  const items: MenuItem[] = [];

  if (documentDetails.value.type !== 'Contract') {
    items.push({
      label: 'Copy to new advertiser',
      icon: PlusIcon,
      command: () => copyAdvertiserVisible.value = true,
    });
  }

  items.push({
    label: documentDetails.value.type === 'Contract' ? 'Open contract' : 'Open document',
    icon: FileIcon,
    command: () => onOpenDocument(),
  });

  return items;
});

const auditTrailLoadedForDocId = ref<number | null>(null);

const {
  makeRequest: makeAuditTrailRequest, setEndpointUrl: auditTrailEndpoint,
} = useEndpoint<ApiResponseBase<DocumentAuditTrailsResponse>>({
  type: 'GET',
  successField: 'data',
  onSuccess: response => {
    const map = response.data.auditTrailsByExternalId || {};

    documentHistory.value = documentHistory.value.map(h => {
      const externalId = h.externalDocumentId;
      if (!externalId) return h;

      return {
        ...h,
        auditTrail: map[externalId] ?? [],
      };
    });

    auditTrailLoadedForDocId.value = props.id;
  },
  toast: { showOnSuccess: false },
});

const {
  isLoading: isDetailLoading, makeRequest: makeDetailRequest, setEndpointUrl: detailRequestEndpoint,
} = useEndpoint<ApiResponseBase<DocumentDetailsData>>({
  type: 'GET',
  successField: 'data',
  onSuccess: response => {
    const { documentDetails: details } = response.data;
    documentDetails.value = response.data.documentDetails;
    documentHistory.value = response.data.documentHistory;
    documentVersions.value = response.data.documentVersions;

    emitEvent<CrmDrawerDataReadyEvent>(EventBusEvents.CrmDetailLoaded, { title: details.description ? details.description : details.displayId, type: 'Document' });

    const hasAnyAudit = documentHistory.value.some(h => (h as any).hasAuditTrail && (h as any).externalDocumentId);
    if (hasAnyAudit) {
      auditTrailEndpoint(`/crm/documents/${props.id}/audit-trails`);
      makeAuditTrailRequest();
    }
  },
  toast: { showOnSuccess: false },
});

const accounts = ref([]);
const {
  isLoading: isAdvertiserToCopyToLoading, makeRequest: makeAdvertiserToCopyToRequest,
} = useEndpoint<ApiResponseBase<AccountToCopyTo>>({
  url: '/crm/documents/accountsToCopyTo',
  type: 'GET',
  successField: 'data',
  onSuccess: response => {
    accounts.value = response.data.accounts;

  },
  toast: { showOnSuccess: false },
});

const {
  makeRequest: makeCopyProposalRequest, setEndpointUrl: copyProposalRequestEndpoint,
} = useEndpoint<ApiResponseBase<CopyToAdvertiserResponse>>({
  type: 'GET',
  successField: 'data',
  onSuccess: response => {
    store.documents.unshift(response.data.newDocuments[0]);
    if (accountStore.useCrmCampaigns) {
      const { basePath } = route.meta;

      router.push({ path: `${basePath}/detail/opportunity/${response.data.opportunityId}` });
    }
    window.open(`/apx/proposalTool/index-continue.html?advertiser=${acctId.value.toString()}&tagid=&pid=${response.data.newDocuments[0].id}`, '_blank');
    acctId.value = 0;
  },
  toast: { showOnSuccess: false },
});

// todo: This currently has to load all leads and opportunities since we need the swimlanes and options from them. It would be nice to be able to split this out to a smaller API call.
const helper = useOpportunities();
helper.loadInitialState();

watch(
  () => props.id,
  newDocumentId => {
    if (newDocumentId) {
      auditTrailLoadedForDocId.value = null;
      documentHistory.value = documentHistory.value.map(h => ({ ...h, auditTrail: []}));

      detailRequestEndpoint(`/crm/documents/${props.id}`);
      makeDetailRequest();
      makeAdvertiserToCopyToRequest();
    }
  },
  { immediate: true },
);

const expandedRows = ref({});
const expandAll = () => {
  expandedRows.value = documentVersions.value.reduce((acc, p) => ((acc[p.version] = true), acc), {});
};
const collapseAll = () => {
  expandedRows.value = null;
};

const onOpenDocument = () => {
  let url = `/apx/proposalTool/index-continue.html?advertiser=${documentDetails.value.entityId.toString()}&tagid=&pid=${documentDetails.value.id}`;

  if (documentDetails.value.type === 'Contract') {
    url = `/util/get/getContractPDFfromCFS.php?recordId=${documentDetails.value.contractId}`;
  }
  window.open(url, '_blank');
};

const scrollHeight = ref('calc(100vh - 120px)');
const accountProposalFilters = ref({
  // eslint-disable-next-line camelcase
  c2_id: { value: null, matchMode: FilterMatchMode.CONTAINS },
  // eslint-disable-next-line camelcase
  c2_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  // eslint-disable-next-line camelcase
  c2_user: { value: null, matchMode: FilterMatchMode.CONTAINS },
  // eslint-disable-next-line camelcase
  c2_addr2: { value: null, matchMode: FilterMatchMode.CONTAINS },
  // eslint-disable-next-line camelcase
  c2_city: { value: null, matchMode: FilterMatchMode.CONTAINS },
  // eslint-disable-next-line camelcase
  c2_state: { value: null, matchMode: FilterMatchMode.CONTAINS },
  // eslint-disable-next-line camelcase
  c2_qb_listid: { value: null, matchMode: FilterMatchMode.CONTAINS },
  // eslint-disable-next-line camelcase
  c2_type: { value: null, matchMode: FilterMatchMode.CONTAINS },
  // eslint-disable-next-line camelcase
  c2_prev_user: { value: null, matchMode: FilterMatchMode.CONTAINS },
  // eslint-disable-next-line camelcase
  c2_agency_text: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

const acctId = ref(0);

const onCopyProposalClick = (e: DataTableRowClickEvent) => {
  acctId.value = e.data.c2_id;
  confirm.require({
    group: 'CopyProposalConfirm',
    header: `Are you sure you want to copy this document to ${e.data.c2_name}?`,
    accept: () => onConfirmCopyProposal(e.data.c2_id),
    position: 'center',
    acceptLabel: 'Copy',
    rejectLabel: 'Cancel',
    rejectClass: 'p-button-secondary',
    acceptProps: {
      dt: greenButton,
    },
  });
};

const onConfirmCopyProposal = (accountId: number) => {
  copyProposalRequestEndpoint(`/apx/TAG/salesdocs/ajax_copy_proposal.php?key=${documentDetails.value.id}&c2id=${accountId}&returnJson=true`);
  makeCopyProposalRequest();
};


const expandedHistoryRows = ref({});

const historyRowClass = (row) => ({
  'no-expansion': !(row.hasAuditTrail || row.auditTrail?.length),
});

const onHistoryRowClick = (e) => {
  const target = e.originalEvent?.target;
  if (target?.closest?.('.p-row-toggler')) return;

  window.open(e.data.link, '_blank');
};

const handleNavigateForward = (context: FlyoutNavigationContext) => {
  const { basePath } = route.meta;
  const path = `${basePath}/detail/${context.view.toLowerCase()}/${context.id}`;
  router.push({ path });
};

</script>
<template>
  <DetailDrawerHeader
    v-if="!isDetailLoading"
    type="Document"
    :menu-items="mobileMenuItems"
    :title="documentDetails.description ? documentDetails.description : documentDetails.displayId"
    :subtitle="documentDetails.description ? documentDetails.displayId : ''"
  >
    <template #buttons>
      <Button
        v-if="documentDetails.type !== 'Contract'"
        severity="secondary"
        class="mr-2"
        outlined
        @click="copyAdvertiserVisible = true"
      >
        <PlusIcon />
        Copy To New Advertiser
      </Button>
      <Button
        severity="primary"
        class="mr-2"
        @click="onOpenDocument"
      >
        <FileIcon />
        {{ documentDetails.type === 'Contract' ? 'Open Contract' : 'Open Document' }}
      </Button>
    </template>
  </DetailDrawerHeader>
  <Tabs
    v-if="!isDetailLoading"
    class="grow"
    :value="activeTab"
    @update:value="(val: CrmDetailTabName) => handleDetailTabUpdate(val, () => activeTab = val)"
  >
    <TabList>
      <Tab value="overview" :class="SharedStyles.flyouts.tabClasses">
        <GlobeIcon />
        Overview
      </Tab>
      <Tab value="relationships" :class="SharedStyles.flyouts.tabClasses">
        <SitemapIcon />
        Relationships
      </Tab>
      <ConfigGate enable-for="useCrmCampaigns">
        <Tab value="deals" :class="SharedStyles.flyouts.tabClasses">
          <BriefcaseIcon />
          Deals &amp; Docs
        </Tab>
      </ConfigGate>
    </TabList>

    <TabPanels class="!bg-[var(--neutral-background)] grow">
      <div class="w-full grid grid-cols-3 grid-flow-row auto-rows-auto lg:gap-x-9 gap-y-4 p-2 lg:p-9">
        <div class="flex flex-col gap-4 col-span-3">
          <TabPanel value="overview">
            <div class="flex flex-col gap-6">
              <OfferSubmissionSection
                v-if="documentDetails.offerSubmission?.showSection"
                class="mb-3"
                :document="{ proposalId: documentDetails.id, displayId: documentDetails.displayId, entityName: documentDetails.entityName }"
                :offer-submission="documentDetails.offerSubmission"
              />

              <Section class="mb-3" :footer="false">
                <DataTable
                  v-model:expanded-rows="expandedHistoryRows"
                  :value="documentHistory"
                  table-style="min-width: 50rem;"
                  class="no-left-padding"
                  :dt="datatable.noLeftPadding"
                  data-key="link"
                  :row-class="historyRowClass"
                  @row-click="onHistoryRowClick"
                >
                  <template #header>
                    <div class="flex items-center justify-between gap-4">
                      <Typography variant="section-heading">
                        Document History
                        <Badge :value="documentHistory.length" severity="secondary" />
                      </Typography>
                    </div>
                  </template>
                  <template #empty>
                    No history found for document
                  </template>
                  <Column expander style="width: 4.5rem" />
                  <Column header="Document" class="text-[var(--text-link-primary)] cursor-pointer">
                    <template #body>
                      <div class="flex justify-center items-center" style="height: 100%; width: 100%;">
                        <FileIcon size="1.5rem" color="var(--apx-dark-gray-400)" />
                      </div>
                    </template>
                  </Column>

                  <Column field="sentAs" header="Sent As" sortable />
                  <Column field="sentBy" header="Sent By" sortable />
                  <Column field="method" header="Method" sortable />
                  <Column
                    field="date"
                    header="Created"
                    sortable
                    :sort-field="'age'"
                  />

                  <Column field="details" header="More Details" sortable>
                    <template #body="slotProps">
                      <!-- eslint-disable-next-line vue/no-v-html -->
                      <span v-html="slotProps.data.hasAuditTrail ? '' : slotProps.data.details" />
                    </template>
                  </Column>

                  <template #expansion="slotProps">
                    <div v-if="slotProps.data.hasAuditTrail" class="p-3">
                      <div class="font-semibold mb-2">
                        Audit Trail
                      </div>
                      <div v-if="!slotProps.data.auditTrail?.length">
                        <Skeleton height="4rem" />
                      </div>
                      <div v-else>
                        <DataTable
                          :value="slotProps.data.auditTrail"
                          table-style="min-width: 40rem;"
                        >
                          <Column header="Date">
                            <template #body="{ data }">
                              {{ new Date(data.date).toLocaleString() }}
                            </template>
                          </Column>

                          <Column field="action" header="Action" />
                          <Column field="user.firstName" header="First Name" />
                          <Column field="user.lastName" header="Last Name" />
                          <Column field="userEmail" header="Email" />

                          <Column header="Reason">
                            <template #body="{ data }">
                              <span v-if="data.reason">{{ data.reason }}</span>
                              <span v-else class="text-muted">—</span>
                            </template>
                          </Column>
                        </DataTable>
                      </div>
                    </div>
                  </template>
                </DataTable>
              </Section>

              <Section class="mb-3" :footer="false">
                <DataTable
                  v-model:expanded-rows="expandedRows"
                  :value="documentVersions"
                  table-style="min-width: 50rem;"
                  class="no-left-padding"
                  data-key="version"
                  :dt="datatable.noLeftPadding"
                >
                  <template #header>
                    <div class="flex items-center justify-between gap-4">
                      <Typography variant="section-heading">
                        Document Versions
                        <Badge :value="documentVersions.length" severity="secondary" />
                      </Typography>
                      <div class="flex flex-wrap justify-end gap-2">
                        <Button
                          text
                          @click="expandAll"
                        >
                          <PlusIcon />
                          Expand All
                        </Button>
                        <Button
                          text
                          @click="collapseAll"
                        >
                          <MinusIcon />
                          Collapse All
                        </Button>
                      </div>
                    </div>
                  </template>
                  <template #empty>
                    No versions found for document
                  </template>
                  <Column expander style="width: 5rem" />
                  <Column field="version" header="Version" sortable />
                  <Column field="type" header="Type" sortable />
                  <Column
                    field="created"
                    header="Created"
                    sortable
                    :sort-field="'createdAt'"
                  />
                  <Column
                    field="lastPublished"
                    header="Last Published"
                    sortable
                    :sort-field="'publishedAge'"
                  />
                  <template #expansion="slotProps">
                    <div class="p-4">
                      <DataTable :value="slotProps.data.inventory">
                        <Column field="inventory" header="Inventory" sortable />
                        <Column field="quantity" header="Qty" sortable />
                        <Column field="class" header="Contract Class" sortable />
                        <Column field="description" header="Description" sortable />
                        <Column field="periods" header="Periods" sortable />
                        <Column field="start" header="Start" sortable />
                        <Column field="end" header="End" sortable />
                        <Column field="rate" header="Rate" sortable />
                        <Column field="production" header="Production Rate" sortable />
                      </DataTable>
                    </div>
                  </template>
                </DataTable>
              </Section>
            </div>
          </TabPanel>

          <TabPanel value="relationships">
            <div class="flex flex-col gap-6">
              <Section class="mb-3" :footer="false">
                <AssociationListContainer
                  title="Accounts"
                  :item-count="documentDetails.entityId ? 1 : 0"
                  :hide-add-button="true"
                  no-items-message="No account associated with this document"
                >
                  <AssociationListItem
                    v-if="documentDetails.entityId"
                    :id="documentDetails.entityId"
                    association-type="account"
                    tab-id="relationships"
                    :title="documentDetails.entityName"
                    :subtitle="documentDetails.entityType"
                    @navigate-forward="handleNavigateForward"
                  />
                </AssociationListContainer>
              </Section>
            </div>
          </TabPanel>

          <TabPanel value="deals">
            <div class="flex flex-col">
              <DocumentDealsDocsPanel
                :campaign-id="documentDetails.campaignId"
                :campaign-name="documentDetails.campaignName"
                :campaign-status="documentDetails.campaignStatus"
                :campaign-type="documentDetails.campaignType"
                :campaign-budget-description="documentDetails.campaignBudgetDescription"
                :campaign-created-date="documentDetails.campaignCreatedDate"
                :campaign-age="documentDetails.campaignAge"
                tab-id="deals"
                @navigate-forward="handleNavigateForward"
              />
            </div>
          </TabPanel>
        </div>
      </div>
    </TabPanels>
  </Tabs>
  <Modal
    title="Select Advertiser To Copy Document To:"
    :visible="copyAdvertiserVisible"
    :disable-accept="true"
    :disable-cancel="true"
    :disable-footer="true"
    @close="copyAdvertiserVisible = false"
  >
    <template #body>
      <FormSkeleton v-if="isAdvertiserToCopyToLoading" />
      <DataTable
        v-else
        v-model:filters="accountProposalFilters"
        :value="accounts"
        filter-display="row"
        table-style="min-width: 50rem;"
        :virtual-scroller-options="{itemSize: 50}"
        striped-rows
        scrollable
        :scroll-height="scrollHeight"
        size="small"
        table-layout="fixed"
        @row-click="onCopyProposalClick"
      >
        <template #empty>
          No history found for document
        </template>
        <Column field="c2_id" header="Id" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              type="text"
              class="p-column-filter"
              placeholder="Ids"
              style="min-width: 8rem; max-width: 8rem"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column field="c2_name" header="Name" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              type="text"
              class="p-column-filter"
              placeholder="Names"
              style="min-width: 8rem; max-width: 8rem"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column field="c2_user" header="Owner" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              type="text"
              class="p-column-filter"
              placeholder="Owners"
              style="min-width: 8rem; max-width: 8rem"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column field="c2_addr2" header="Address" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              type="text"
              class="p-column-filter"
              placeholder="Addresses"
              style="min-width: 8rem; max-width: 8rem"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column field="c2_city" header="City" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              type="text"
              class="p-column-filter"
              placeholder="Cities"
              style="min-width: 8rem; max-width: 8rem"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column field="c2_state" header="State" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              type="text"
              class="p-column-filter"
              placeholder="States"
              style="min-width: 8rem; max-width: 8rem"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column field="c2_qb_listid" header="AR Code" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              type="text"
              class="p-column-filter"
              placeholder="Codes"
              style="min-width: 8rem; max-width: 8rem"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column field="c2_type" header="Type" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              type="text"
              class="p-column-filter"
              placeholder="Types"
              style="min-width: 8rem; max-width: 8rem"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column field="c2_prev_user" header="Prev Owner" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              type="text"
              class="p-column-filter"
              placeholder="Previous Owners"
              style="min-width: 8rem; max-width: 8rem"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column field="c2_agency_text" header="Agency" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              type="text"
              class="p-column-filter"
              placeholder="Agencies"
              style="min-width: 8rem; max-width: 8rem"
              @input="filterCallback()"
            />
          </template>
        </Column>
      </DataTable>
    </template>
  </Modal>
  <ConfirmDialog group="CopyProposalConfirm" />
</template>

<style scoped>
:deep(.p-datatable-tbody > tr.no-expansion .p-datatable-row-toggle-button),
:deep(.p-datatable-body  > tr.no-expansion .p-datatable-row-toggle-button) {
  display: none !important;
}
</style>

